import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, FileText, Download, Map, Camera, Calendar, Book, Compass, Search, Filter, ChevronDown, ChevronUp } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import ResourceDownloadForm from '../components/ResourceDownloadForm';

interface Resource {
  id: string;
  title: string;
  type: string;
  description: string;
  image: string;
  downloadCount: number;
  fileSize: string;
  tags: string[];
}

const resources: Resource[] = [
  {
    id: 'gorilla-guide',
    title: 'Ultimate Gorilla Trekking Guide',
    type: 'PDF Guide',
    description: 'Complete guide to planning your gorilla trekking adventure in Uganda, including permit information, packing lists, and photography tips.',
    image: 'https://imgur.com/LUalhrs.jpg',
    downloadCount: 1245,
    fileSize: '4.2 MB',
    tags: ['Gorilla Trekking', 'Wildlife', 'Planning']
  },
  {
    id: 'uganda-map',
    title: 'Uganda National Parks Map',
    type: 'Printable Map',
    description: 'Detailed map of Uganda\'s national parks with key attractions, wildlife viewing spots, and travel distances.',
    image: 'https://imgur.com/TvdP6Mt.jpg',
    downloadCount: 987,
    fileSize: '3.8 MB',
    tags: ['Maps', 'National Parks', 'Planning']
  },
  {
    id: 'safari-checklist',
    title: 'Uganda Safari Packing Checklist',
    type: 'PDF Checklist',
    description: 'Comprehensive packing list for your Uganda safari adventure, customized for different seasons and activities.',
    image: 'https://imgur.com/WqH4Bly.jpg',
    downloadCount: 856,
    fileSize: '1.5 MB',
    tags: ['Safari', 'Packing', 'Planning']
  },
  {
    id: 'photo-guide',
    title: 'Wildlife Photography Guide',
    type: 'PDF Guide',
    description: 'Expert tips for capturing stunning wildlife photos in Uganda, including camera settings and positioning techniques.',
    image: 'https://imgur.com/Rl4EEiV.jpg',
    downloadCount: 723,
    fileSize: '5.1 MB',
    tags: ['Photography', 'Wildlife', 'Tips']
  },
  {
    id: 'self-drive',
    title: 'Self-Drive Safari Handbook',
    type: 'PDF Guide',
    description: 'Everything you need to know about planning and executing a self-drive safari in Uganda, including routes and safety tips.',
    image: 'https://imgur.com/YABl8r4.jpg',
    downloadCount: 689,
    fileSize: '6.3 MB',
    tags: ['Self-Drive', 'Safari', 'Planning']
  },
  {
    id: 'birdwatching',
    title: 'Uganda Birdwatching Guide',
    type: 'PDF Guide',
    description: 'Comprehensive guide to Uganda\'s 1,000+ bird species, including identification tips and best viewing locations.',
    image: 'https://imgur.com/7qDwljW.jpg',
    downloadCount: 542,
    fileSize: '7.8 MB',
    tags: ['Birdwatching', 'Wildlife', 'Tips']
  },
  {
    id: 'cultural-guide',
    title: 'Uganda Cultural Experiences Guide',
    type: 'PDF Guide',
    description: 'Explore Uganda\'s rich cultural heritage with this guide to traditional ceremonies, crafts, and community visits.',
    image: 'https://imgur.com/5v6lVl8.jpg',
    downloadCount: 478,
    fileSize: '3.5 MB',
    tags: ['Culture', 'Communities', 'Experiences']
  },
  {
    id: 'kampala-guide',
    title: 'Kampala City Guide',
    type: 'PDF Guide',
    description: 'Insider\'s guide to Uganda\'s vibrant capital, featuring hidden gems, local eateries, and cultural attractions.',
    image: 'https://imgur.com/EzHnSsj.jpg',
    downloadCount: 623,
    fileSize: '4.7 MB',
    tags: ['City Tours', 'Kampala', 'Urban']
  }
];

const Resources = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [selectedResource, setSelectedResource] = useState<Resource | null>(null);
  const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false);

  // Get all unique tags
  const allTags = Array.from(new Set(resources.flatMap(resource => resource.tags)));

  // Filter resources based on search and tags
  const filteredResources = resources.filter(resource => {
    const matchesSearch = resource.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         resource.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         resource.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()));
    
    const matchesTags = selectedTags.length === 0 ||
                       selectedTags.some(tag => resource.tags.includes(tag));
    
    return matchesSearch && matchesTags;
  });

  const handleDownload = (resource: Resource) => {
    setSelectedResource(resource);
    setIsDownloadModalOpen(true);
  };

  const toggleTag = (tag: string) => {
    if (selectedTags.includes(tag)) {
      setSelectedTags(selectedTags.filter(t => t !== tag));
    } else {
      setSelectedTags([...selectedTags, tag]);
    }
  };

  return (
    <>
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative bg-gray-900 text-white">
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1516426122078-c23e76319801"
            alt="Uganda resources"
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/50 to-black/80"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 py-16 sm:px-6 lg:px-8">
          <div className="mb-4">
            <Link to="/" className="inline-flex items-center text-white/90 hover:text-white">
              <ArrowLeft className="h-4 w-4 mr-1" />
              Back to Home
            </Link>
          </div>
          
          <div className="max-w-3xl">
            <h1 className="text-4xl font-bold mb-4">Uganda Travel Resources</h1>
            <p className="text-lg text-white/90 mb-6">
              Free downloadable guides, maps, and checklists to help you plan the perfect Uganda safari adventure.
            </p>
            
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search resources..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 w-full rounded-lg bg-white/10 border border-white/20 text-white placeholder-white/60 focus:ring-ta-green focus:border-ta-green"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Filters Section */}
      <section className="bg-white border-b border-gray-200 sticky top-14 z-20">
        <div className="max-w-7xl mx-auto px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <button
              onClick={() => setIsFilterOpen(!isFilterOpen)}
              className="flex items-center px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              <Filter className="h-4 w-4 mr-2" />
              Filter by Topic
              {isFilterOpen ? (
                <ChevronUp className="ml-2 h-4 w-4" />
              ) : (
                <ChevronDown className="ml-2 h-4 w-4" />
              )}
            </button>
            
            <div className="text-sm text-gray-500">
              {filteredResources.length} resources available
            </div>
          </div>

          {isFilterOpen && (
            <div className="mt-4 pt-4 border-t border-gray-200">
              <div className="flex flex-wrap gap-2">
                {allTags.map((tag) => (
                  <button
                    key={tag}
                    onClick={() => toggleTag(tag)}
                    className={`px-3 py-1.5 rounded-full text-sm transition-colors ${
                      selectedTags.includes(tag)
                        ? 'bg-ta-green text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {tag}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Resources Grid */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredResources.map((resource) => (
              <div 
                key={resource.id}
                className="bg-white rounded-lg border border-gray-200 overflow-hidden hover:shadow-lg transition-all duration-300"
              >
                <div className="relative h-40">
                  <img 
                    src={resource.image}
                    alt={resource.title}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute top-2 left-2 bg-white/90 backdrop-blur-sm px-2 py-1 rounded-full text-xs font-medium">
                    {resource.type}
                  </div>
                </div>
                
                <div className="p-4">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{resource.title}</h3>
                  <p className="text-sm text-gray-600 mb-4 line-clamp-2">{resource.description}</p>
                  
                  <div className="flex flex-wrap gap-2 mb-4">
                    {resource.tags.map((tag, index) => (
                      <span 
                        key={index}
                        className="px-2 py-1 bg-gray-100 text-gray-700 rounded-full text-xs"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                  
                  <div className="flex items-center justify-between text-sm text-gray-500">
                    <span>{resource.fileSize}</span>
                    <span>{resource.downloadCount} downloads</span>
                  </div>
                  
                  <button
                    onClick={() => handleDownload(resource)}
                    className="mt-4 w-full py-2 bg-ta-green text-white rounded-lg hover:bg-ta-green-dark transition-colors flex items-center justify-center"
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Download Free Guide
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Resource Categories */}
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-8">Resource Categories</h2>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-gray-50 p-6 rounded-lg hover:shadow-md transition-all duration-300">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-ta-green/10 rounded-lg flex items-center justify-center">
                  <Camera className="h-6 w-6 text-ta-green" />
                </div>
                <h3 className="ml-3 text-lg font-semibold text-gray-900">Wildlife Guides</h3>
              </div>
              <p className="text-gray-600 mb-4">
                Resources for gorilla trekking, safari planning, and wildlife viewing in Uganda.
              </p>
              <button
                onClick={() => {
                  setSelectedTags(['Wildlife', 'Gorilla Trekking', 'Safari']);
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
                className="text-ta-green hover:text-ta-green-dark text-sm"
              >
                View Wildlife Resources
              </button>
            </div>
            
            <div className="bg-gray-50 p-6 rounded-lg hover:shadow-md transition-all duration-300">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-ta-green/10 rounded-lg flex items-center justify-center">
                  <Map className="h-6 w-6 text-ta-green" />
                </div>
                <h3 className="ml-3 text-lg font-semibold text-gray-900">Maps & Routes</h3>
              </div>
              <p className="text-gray-600 mb-4">
                Downloadable maps, self-drive routes, and navigation resources for Uganda travel.
              </p>
              <button
                onClick={() => {
                  setSelectedTags(['Maps', 'Self-Drive']);
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
                className="text-ta-green hover:text-ta-green-dark text-sm"
              >
                View Maps & Routes
              </button>
            </div>
            
            <div className="bg-gray-50 p-6 rounded-lg hover:shadow-md transition-all duration-300">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-ta-green/10 rounded-lg flex items-center justify-center">
                  <Calendar className="h-6 w-6 text-ta-green" />
                </div>
                <h3 className="ml-3 text-lg font-semibold text-gray-900">Trip Planning</h3>
              </div>
              <p className="text-gray-600 mb-4">
                Checklists, itineraries, and planning tools for your Uganda adventure.
              </p>
              <button
                onClick={() => {
                  setSelectedTags(['Planning', 'Packing']);
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
                className="text-ta-green hover:text-ta-green-dark text-sm"
              >
                View Planning Resources
              </button>
            </div>
            
            <div className="bg-gray-50 p-6 rounded-lg hover:shadow-md transition-all duration-300">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-ta-green/10 rounded-lg flex items-center justify-center">
                  <Book className="h-6 w-6 text-ta-green" />
                </div>
                <h3 className="ml-3 text-lg font-semibold text-gray-900">City & Culture</h3>
              </div>
              <p className="text-gray-600 mb-4">
                Guides to Uganda's cities, cultural experiences, and local customs.
              </p>
              <button
                onClick={() => {
                  setSelectedTags(['City Tours', 'Culture', 'Urban']);
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
                className="text-ta-green hover:text-ta-green-dark text-sm"
              >
                View City & Culture Resources
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Embed Section */}
      <section className="py-12 bg-gray-50 border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-8">Share Our Resources</h2>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Embed on Your Website</h3>
              <p className="text-gray-600 mb-6">
                Help your readers plan their Uganda safari by embedding our resources on your website or blog. 
                Simply copy and paste the code below:
              </p>
              
              <div className="bg-gray-100 p-4 rounded-lg mb-4">
                <pre className="text-xs text-gray-700 overflow-x-auto">
                  {`<iframe src="https://travelcord.guide/resources-embed" width="100%" height="450" frameborder="0"></iframe>`}
                </pre>
              </div>
              
              <button
                onClick={() => {
                  navigator.clipboard.writeText('<iframe src="https://travelcord.guide/resources-embed" width="100%" height="450" frameborder="0"></iframe>');
                  alert('Embed code copied to clipboard!');
                }}
                className="px-4 py-2 bg-ta-green text-white rounded-lg hover:bg-ta-green-dark transition-colors"
              >
                Copy Embed Code
              </button>
            </div>
            
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Link to Our Resources</h3>
              <p className="text-gray-600 mb-6">
                If you find our resources valuable, please consider linking to them from your website. 
                This helps other travelers discover useful information for their Uganda safari planning.
              </p>
              
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                <h4 className="font-medium text-gray-900 mb-2">Suggested Link Text:</h4>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li>• "Free Uganda Safari Planning Resources"</li>
                  <li>• "Uganda Gorilla Trekking Guides & Resources"</li>
                  <li>• "Comprehensive Uganda Travel Resources"</li>
                  <li>• "Uganda Self-Drive Safari Resources"</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Download Modal */}
      {selectedResource && (
        <ResourceDownloadForm
          isOpen={isDownloadModalOpen}
          onClose={() => setIsDownloadModalOpen(false)}
          resourceTitle={selectedResource.title}
          resourceType={selectedResource.type}
          resourceDescription={selectedResource.description}
        />
      )}

      <Footer />
    </>
  );
};

export default Resources;